import { environment } from './environment'
import { constants } from './constants'
import store from './redux/store'
import axios from 'axios'
import { propertyActions } from './redux/actions/property'

const apiBaseUrl = environment.CONFIG_API_BASE_URL
const apiBaseUrlV2 = environment.CONFIG_API_BASE_URL_V2

let accessToken = store.getState().user.hasOwnProperty('accessToken') ? store.getState().user.accessToken : ''

export const apiV2ErrorCodes = {
  'ROOM_CODE_ALREADY_EXISTS': 1001
}

store.subscribe(() => {
  const userStore = store.getState().user
  const newAccessToken = userStore.hasOwnProperty('accessToken') ? userStore.accessToken : ''
  if (accessToken !== newAccessToken) {
    accessToken = newAccessToken
  }
})

const fetchThenHandler = (response) => {
  if (response.status === 403) {
    throw new Error('NOT_AUTHORIZED')
  }
  if (!response.ok) {
    throw new Error('request failed with status: ' + response.status + ', ' + response.json)
  }
  return response.json()
}

const propertyPublishStatusRefreshHandler = (giataId) => {
  return (response) => {
    if (response.ok || response.status < 300) {
      store.dispatch(propertyActions.refreshPublishStatus(giataId))
    }
    return response
  }
}

const fetchThenNoBodyHandler = (response) => {
  if (response.status === 403) {
    throw new Error('NOT_AUTHORIZED')
  }
  if (!response.ok) {
    throw new Error('request failed with status: ' + response.status + ', ' + response.json)
  }
}

export const authenticate = (idToken, abortController = new AbortController()) => {
  const fetchPromise = fetch(apiBaseUrl + constants.urls.auth, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-Auth-Token': idToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const authenticateRefresh = (abortController = new AbortController()) => {
  const fetchPromise = fetch(apiBaseUrl + constants.urls.authRefresh, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

/**
 *
 * @param {string} supplierId
 * @param {string} code
 * @param {string|null} email
 * @param abortController
 * @returns {{fetchPromise: (*|Promise<Response>), abortController: AbortController}}
 */
export const authenticateThirdPartySupplier = (supplierId, code, email, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.authSupplier
  url = url.replace('{supplierId}', supplierId)
  url = url.replace('{code}', encodeURIComponent(code))

  if (email !== null) {
    url += '?email=' + encodeURIComponent(email)
  }
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
    },
    signal: abortController.signal
  })
  return { fetchPromise, abortController }
}

export const getHotelierPropertyList = (limit, offset, propertyNameFilter = null, abortController = new AbortController()) => {
  const url = new URL(apiBaseUrlV2 + constants.urls.hotelierPropertyList)
  url.searchParams.set('limit', limit)
  url.searchParams.set('offset', offset)
  if (propertyNameFilter) {
    url.searchParams.set('propertyNameFilter', propertyNameFilter)
  }
  const fetchPromise = fetch(
    url,
    {
      method: 'GET',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
        'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
      },
      signal: abortController.signal,
    }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getGiataProperty = (giataId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.esProperties.property
  url = url.replace('{giataId}', giataId)
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getGiataPropertyImages = (giataId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.images
  url = url.replace('{giataId}', giataId)
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getPublicHotelierProperty = (giataId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.publicHotelierProperties.property
  url = url.replace('{giataId}', giataId)
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getBlueprint = (giataId, abortController = new AbortController()) => {
  let url = apiBaseUrl + constants.urls.giataProperties.constants.blueprint
  url = url.replace('{giataId}', giataId)
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getRooms = (giataId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.rooms
  url = url.replace('{giataId}', giataId)
  url += '?details=1'
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getPermissionsByGiataId = (giataId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.permissions
  url = url.replace('{giataId}', giataId)
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getDashboardToken = (giataId, abortController = new AbortController()) => {
  let url = apiBaseUrl + constants.urls.giataProperties.dashboard.token
  url = url.replace('{giataId}', giataId)
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getFlowtifyByGiataId = (giataId, abortController = new AbortController()) => {
  let url = apiBaseUrl + constants.urls.giataProperties.flowtify
  url = url.replace('{giataId}', giataId)
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const deleteImages = (giataId, imageIds, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.images
  url = url.replace('{giataId}', giataId)
  const patchObject = {}
  imageIds.forEach((imageId) => {
    patchObject[imageId] = {
      deleted: true
    }
  })
  const fetchPromise = fetch(url, {
    method: 'PATCH',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken,
    },
    body: JSON.stringify(patchObject),
    signal: abortController.signal
  }).then(propertyPublishStatusRefreshHandler(giataId))
    .then(fetchThenNoBodyHandler)
  return { fetchPromise, abortController }
}

/**
 * @param giataId
 * @param imageId null to delete
 * @param abortController
 */
export const updateHeroImageForGiataId = (giataId, imageId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.gallery
  url = url.replace('{giataId}', giataId)

  const method = 'PATCH'
  const fetchPromise = fetch(url, {
    method: method,
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    body: JSON.stringify({ heroImage: imageId }),
    signal: abortController.signal
  }).then(propertyPublishStatusRefreshHandler(giataId))
    .then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const patchImage = (giataId, imageId, body, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.image
  url = url.replace('{giataId}', giataId)
  url = url.replace('{imageId}', imageId)
  const fetchPromise = fetch(url, {
    method: 'PATCH',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    body: JSON.stringify(body),
    signal: abortController.signal
  }).then(propertyPublishStatusRefreshHandler(giataId))
    .then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const reOrderImages = (giataId, imageIdsOrdered, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.gallery
  url = url.replace('{giataId}', giataId)
  const fetchPromise = fetch(url, {
    method: 'PATCH',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    body: JSON.stringify({ order: imageIdsOrdered }), // yes body here is no error, apparently it is like that
    signal: abortController.signal
  }).then(propertyPublishStatusRefreshHandler(giataId))
    .then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const deleteRejectInfo = (imageId, giataId, abortController = new AbortController()) => {
  let url = apiBaseUrl + constants.urls.giataProperties.rejectImageInfo
  url = url.replace('{giataId}', giataId)
  url = url.replace('{imageId}', imageId)

  const fetchPromise = fetch(url, {
    method: 'DELETE',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(propertyPublishStatusRefreshHandler(giataId))
    .then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const putRejectImageInfo = (imageId, giataId, info, abortController = new AbortController()) => {
  let url = apiBaseUrl + constants.urls.giataProperties.rejectImageInfo
  url = url.replace('{giataId}', giataId)
  url = url.replace('{imageId}', imageId)

  const fetchPromise = fetch(url, {
    method: 'PUT',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    body: JSON.stringify(info),
    signal: abortController.signal
  }).then(propertyPublishStatusRefreshHandler(giataId))
    .then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const saveAndConfirmImages = (giataId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.confirm + '/' + giataId

  const fetchPromise = fetch(url, {
    method: 'POST',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(propertyPublishStatusRefreshHandler(giataId))
    .then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const createRoom = (giataId, body, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.rooms
  url = url.replace('{giataId}', giataId)

  const fetchPromise = fetch(url, {
    method: 'POST',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    body: JSON.stringify(body),
    signal: abortController.signal
  })
    .then(propertyPublishStatusRefreshHandler(giataId))
  return { fetchPromise, abortController }
}

export const deleteRoom = (giataId, roomId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.room
  url = url.replace('{giataId}', giataId)
  url = url.replace('{roomId}', roomId)

  const fetchPromise = fetch(url, {
    method: 'DELETE',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(propertyPublishStatusRefreshHandler(giataId))
    .then(fetchThenNoBodyHandler)
  return { fetchPromise, abortController }
}

export const putRoom = (giataId, roomId, body, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.room
  url = url.replace('{giataId}', giataId)
  url = url.replace('{roomId}', roomId)

  const fetchPromise = fetch(url, {
    method: 'PUT',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    body: JSON.stringify(body),
    signal: abortController.signal
  })
    .then(propertyPublishStatusRefreshHandler(giataId))
  return { fetchPromise, abortController }
}

export const addRoomToImageRelation = (giataId, imageId, roomId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.imageRoomBinding
  url = url.replace('{giataId}', giataId)
  url = url.replace('{imageId}', imageId)
  url = url.replace('{roomId}', roomId)

  const fetchPromise = fetch(url, {
    method: 'POST',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  })
    .then(propertyPublishStatusRefreshHandler(giataId))
    .then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const deleteRoomToImageRelation = (giataId, imageId, roomId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.imageRoomBinding
  url = url.replace('{giataId}', giataId)
  url = url.replace('{imageId}', imageId)
  url = url.replace('{roomId}', roomId)

  const fetchPromise = fetch(url, {
    method: 'DELETE',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(propertyPublishStatusRefreshHandler(giataId))
    .then(fetchThenNoBodyHandler)
  return { fetchPromise, abortController }
}

export const getGiataHotelierProperty = (giataId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.property
  url = url.replace('{giataId}', giataId)
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getCountries = (abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.countries
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getFactsheetBlueprint = (abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.blueprints.factsheet
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const putGiataHotelierProperty = (giataId, body, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.property
  url = url.replace('{giataId}', giataId)

  const fetchPromise = fetch(url, {
    method: 'PUT',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    body: JSON.stringify(body),
    signal: abortController.signal
  }).then(propertyPublishStatusRefreshHandler(giataId))
    .then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getHashInfoByHashAndType = (hash, type, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hash
  url = url.replace('{hash}', hash)
  url = url.replace('{type}', type)
  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
  return { fetchPromise, abortController }
}

export const getHotelierPropertyFactsheetInitial = (giataId, abortController = new AbortController()) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.factsheetInitial
  url = url.replace('{giataId}', giataId)

  const fetchPromise = fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then((response) => {
    if (response.status === 403) {
      throw new Error('NOT_AUTHORIZED')
    }
    if (response.status === 404) {
      return { facts: {} }
    }
    if (!response.ok) {
      throw new Error('request failed with status: ' + response.status + ', ' + response.json)
    }
    return response.json()
  })
  return { fetchPromise, abortController }
}

export const headMyGiataDriveProHotel = (giataId, abortController = new AbortController()) => {
  const url = 'https://giatadrive.com/' + giataId + '/json'
  const fetchPromise = fetch(url, {
    method: 'HEAD',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json'
    },
    signal: abortController.signal
  })
  return { fetchPromise, abortController }
}

export const uploadImage = (giataId, file, roomId, onUploadProgress) => {
  let url = apiBaseUrlV2 + constants.urls.hotelierProperties.images
  url = url.replace('{giataId}', giataId)
  let data = new FormData()
  data.append('image', file)
  roomId && data.append('roomId', roomId)
  const cancelTokenSource = axios.CancelToken.source()

  const promise = axios.post(url, data, {
    onUploadProgress,
    headers: {
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    cancelToken: cancelTokenSource.token
  })
    .then(propertyPublishStatusRefreshHandler(giataId))

  return { promise, cancelTokenSource }
}

export const getDistributionChannels = () => {
  let url = apiBaseUrlV2 + constants.urls.distributionChannels
  return fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
  }).then(fetchThenHandler)
}

export const getPropertyPublishStatus = (giataId, abortController = new AbortController()) => {
  const url = apiBaseUrlV2 + constants.urls.hotelierProperties.publishStatus.replace('{giataId}', giataId)
  return fetch(url, {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  }).then(fetchThenHandler)
}

export const publishProperty = (giataId, abortController = new AbortController()) => {
  const url = apiBaseUrlV2 + constants.urls.hotelierProperties.publish.replace('{giataId}', giataId)
  return fetch(url, {
    method: 'POST',
    cache: 'no-cache',
    headers: {
      'Content-Type': 'application/json',
      'X-GIATA-JWT-TOKEN': 'Bearer ' + accessToken
    },
    signal: abortController.signal
  })
    .then(fetchThenNoBodyHandler)
}
